type DevBridgeValues = {
  uk: string;
  babyIdx: string;
  familyIdx: string;
  skipIntro: boolean;
};

type BridgeWindow = Window & {
  dbd_aos?: { getUserData?: () => unknown };
  TheBabyfolio?: { postMessage?: (message: string) => void };
  webkit?: {
    messageHandlers?: {
      TheBabyfolio?: { postMessage?: (message: string) => void };
      finish?: { postMessage?: (message: string) => void };
    };
  };
  ssckUK?: string;
  ssckBabyIdx?: string;
  ssckFamilyIdx?: string;
};

// Local browser testing only.
// Fill these values in apps/web/.env.test when testing on http://localhost:8788.
const LOCALHOST_DEV_BRIDGE: DevBridgeValues = {
  uk: import.meta.env.VITE_LOCALHOST_UK ?? '',
  babyIdx: import.meta.env.VITE_LOCALHOST_BABY_IDX ?? '',
  familyIdx: import.meta.env.VITE_LOCALHOST_FAMILY_IDX ?? '',
  skipIntro: import.meta.env.VITE_LOCALHOST_SKIP_INTRO !== 'false',
};

// Disposable browser-order test account. The query gate prevents accidental activation,
// but these values are still visible in the built client bundle and must not be a real account.
const PRINTS_BROWSER_TEST_QUERY_KEY = 'prints_test';
const PRINTS_BROWSER_TEST_QUERY_VALUE = 'prt_7Nf4kQ9s2Mx8Wd6Lc3Va1Rb5Hy0GpE';
const PRINTS_BROWSER_TEST_SESSION_KEY = 'termreport:prints:browser-test';
const PRINTS_BROWSER_TEST_BRIDGE: DevBridgeValues = {
  uk: 'cHllaFJLaVVpOVZjZmM1a2dHMys0di9rejhPYjZJTzRIa2dxN0h4Y0Roaz0=',
  babyIdx: '783508',
  familyIdx: '',
  skipIntro: true,
};

const normalize = (value: string): string => value.replace(/[\r\n]+/g, '').trim();

export const isLocalhostHost = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';

const isPrintsRoute = (url: URL): boolean => {
  const pathname = url.pathname.length > 1 && url.pathname.endsWith('/')
    ? url.pathname.slice(0, -1)
    : url.pathname;
  const page = url.searchParams.get('page');
  return pathname === '/prints' || pathname === '/printv2' || page === 'prints' || page === 'printv2';
};

const hasPrintsBrowserTestSession = (): boolean => {
  try {
    return window.sessionStorage.getItem(PRINTS_BROWSER_TEST_SESSION_KEY)
      === PRINTS_BROWSER_TEST_QUERY_VALUE;
  } catch {
    return false;
  }
};

const enablePrintsBrowserTestSession = (url: URL): void => {
  try {
    window.sessionStorage.setItem(
      PRINTS_BROWSER_TEST_SESSION_KEY,
      PRINTS_BROWSER_TEST_QUERY_VALUE,
    );
  } catch {
    // sessionStorage를 사용할 수 없어도 현재 페이지에서는 전역 브릿지 값을 사용할 수 있다.
  }

  url.searchParams.delete(PRINTS_BROWSER_TEST_QUERY_KEY);
  const nextSearch = url.searchParams.toString();
  window.history.replaceState(
    null,
    '',
    `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`,
  );
};

const isPrintsBrowserTest = (): boolean => {
  const url = new URL(window.location.href);
  if (!isPrintsRoute(url)) return false;

  const hasValidQuery = url.searchParams.get(PRINTS_BROWSER_TEST_QUERY_KEY)
    === PRINTS_BROWSER_TEST_QUERY_VALUE;
  if (hasValidQuery) {
    enablePrintsBrowserTestSession(url);
    return true;
  }

  return hasPrintsBrowserTestSession();
};

export const initializeDevBridgeOverrides = () => {
  if (typeof window === 'undefined') return;
  const bridgeWindow = window as BridgeWindow;
  const localhostEnabled = isLocalhostHost(window.location.hostname);
  const hasNativeBridge = typeof bridgeWindow.dbd_aos?.getUserData === 'function'
    || typeof bridgeWindow.TheBabyfolio?.postMessage === 'function'
    || typeof bridgeWindow.webkit?.messageHandlers?.TheBabyfolio?.postMessage === 'function'
    || typeof bridgeWindow.webkit?.messageHandlers?.finish?.postMessage === 'function';

  // 앱의 UK와 테스트 계정의 babyIdx가 섞이지 않도록 네이티브 주입값을 보존한다.
  if (hasNativeBridge && !localhostEnabled) {
    try {
      window.sessionStorage.removeItem(PRINTS_BROWSER_TEST_SESSION_KEY);
    } catch {
      // 저장소 접근이 차단되어도 앱 계정을 테스트 값으로 덮어쓰지 않는다.
    }
    return;
  }

  const browserTestEnabled = isPrintsBrowserTest();
  if (!browserTestEnabled && !localhostEnabled) return;

  const source = browserTestEnabled ? PRINTS_BROWSER_TEST_BRIDGE : LOCALHOST_DEV_BRIDGE;

  const values = {
    uk: normalize(source.uk),
    babyIdx: normalize(source.babyIdx),
    familyIdx: normalize(source.familyIdx),
    skipIntro: source.skipIntro,
  };

  if (values.uk) bridgeWindow.ssckUK = values.uk;
  if (values.babyIdx) bridgeWindow.ssckBabyIdx = values.babyIdx;
  if (values.familyIdx) bridgeWindow.ssckFamilyIdx = values.familyIdx;
  if (values.skipIntro && values.uk) {
    window.localStorage.setItem('introSeen_v1', '1');
  }
};
